import { RawReportPayload, Report, Problem, Prescription, Severity, ProblemKind, HealthDimension, GroupedPrescriptions, EnhancedEvidence } from '../types/report';

/**
 * Parse percentage string or number to number (84.3% -> 84.3, 84.3 -> 84.3)
 */
export function parsePercent(value: string | number): number {
    if (value === null || value === undefined) return 0;

    // Handle number input directly
    if (typeof value === 'number') {
        return isNaN(value) ? 0 : value;
    }

    // Handle string input (percentage format)
    if (typeof value === 'string') {
        const cleaned = value.replace('%', '').trim();
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
    }

    return 0;
}

/**
 * Generate stable ID from issue content
 */
function generateProblemId(issue: string | undefined, index: number): string {
    // Simple hash for stable IDs - could be enhanced with proper hash function
    if (!issue) return `problem-unknown-${index}`;
    const hash = issue.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 20);
    return `problem-${hash}-${index}`;
}

/**
 * Derive problem kind from issue text patterns
 */
function deriveProblemKind(issue: string | undefined, problemDetails?: string): ProblemKind {
    if (!issue) return 'column_completeness';
    const text = `${issue} ${problemDetails || ''}`.toLowerCase();

    if (text.includes('enterprise-wide') || text.includes('across all columns')) {
        return 'enterprise_completeness';
    }
    if (text.includes('standardization') || text.includes('variations') || text.includes('inconsistent')) {
        return 'standardization';
    }
    if (text.includes('pattern changed') || text.includes('different data patterns')) {
        return 'pattern_change';
    }
    if (text.includes('completeness') || text.includes('missing')) {
        return 'column_completeness';
    }

    return 'column_completeness'; // default fallback
}

/**
 * Derive severity from problem content and metrics
 */
function deriveSeverity(problem: any): Severity {
    const technicalAnalysis = problem.Technical_Analysis || problem.technical_analysis || {};
    const missingPercent = technicalAnalysis.Missing_Percentage
        ? parsePercent(technicalAnalysis.Missing_Percentage)
        : 0;

    const issue = (problem.Issue || problem.issue || '').toLowerCase();

    if (issue.includes('critical') || missingPercent > 70) return 'critical';
    if (issue.includes('enterprise-wide') || missingPercent > 40) return 'high';
    if (missingPercent > 20) return 'medium';
    return 'low';
}

/**
 * Strip emojis from action plan text
 */
function cleanActionPlanText(text: string): string {
    return text.replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim();
}

/**
 * Infer quality dimensions from problem content
 */
function inferDimensions(issue: string, problemDetails?: string): import('../types/report').DataQualityDimension[] {
    const text = `${issue} ${problemDetails || ''}`.toLowerCase();
    const dimensions: import('../types/report').DataQualityDimension[] = [];

    if (text.includes('missing') || text.includes('null') || text.includes('empty') || text.includes('completeness')) {
        dimensions.push('completeness');
    }
    if (text.includes('accuracy') || text.includes('correct') || text.includes('valid') || text.includes('invalid')) {
        dimensions.push('accuracy');
    }
    if (text.includes('consistency') || text.includes('standardization') || text.includes('variations')) {
        dimensions.push('consistency');
    }
    if (text.includes('timeliness') || text.includes('timestamp') || text.includes('date')) {
        dimensions.push('timeliness');
    }
    if (text.includes('uniqueness') || text.includes('duplicate') || text.includes('unique')) {
        dimensions.push('uniqueness');
    }
    if (text.includes('validity') || text.includes('format') || text.includes('pattern')) {
        dimensions.push('validity');
    }

    return dimensions.length > 0 ? dimensions : ['completeness'];
}

/**
 * Extract probable cause from problem details and technical analysis
 */
function extractCause(problemDetails?: string, technicalAnalysis?: any): string {
    if (technicalAnalysis && typeof technicalAnalysis === 'object') {
        // Look for specific cause indicators in technical analysis
        if (technicalAnalysis.Missing_Percentage) {
            return `Data entry gaps detected with ${technicalAnalysis.Missing_Percentage} missing values`;
        }
        if (technicalAnalysis.Uniqueness_Ratio) {
            return `Data uniqueness issue with ${technicalAnalysis.Uniqueness_Ratio} uniqueness ratio`;
        }
    }

    if (problemDetails) {
        // Extract first sentence as likely cause
        const firstSentence = problemDetails.split('.')[0];
        if (firstSentence && firstSentence.length > 10) {
            return firstSentence.trim();
        }
    }

    return 'Root cause analysis in progress';
}

/**
 * Infer confidence level from severity
 */
function inferConfidence(severity: import('../types/report').Severity): 'low' | 'medium' | 'high' {
    switch (severity) {
        case 'critical':
        case 'high':
            return 'high';
        case 'medium':
            return 'medium';
        case 'low':
        default:
            return 'medium';
    }
}

/**
 * Generate tags from issue content and problem kind
 */
function generateTags(issue: string, kind: import('../types/report').ProblemKind): string[] {
    const tags: string[] = [];

    // Add kind-based tag
    tags.push(kind.replace('_', ' '));

    const issueText = issue.toLowerCase();
    if (issueText.includes('missing')) tags.push('missing data');
    if (issueText.includes('enterprise')) tags.push('enterprise-wide');
    if (issueText.includes('column')) tags.push('column-specific');
    if (issueText.includes('standardization')) tags.push('standardization');
    if (issueText.includes('quality')) tags.push('data quality');

    return tags;
}

/**
 * Extract column name from issue text if present
 */
function extractColumnFromIssue(issue: string): string | undefined {
    // Look for patterns like "column 'name'" or "field 'name'"
    const columnMatch = issue.match(/column\s+['"`]([^'"`]+)['"`]/i) || issue.match(/field\s+['"`]([^'"`]+)['"`]/i);
    if (columnMatch) {
        return columnMatch[1];
    }

    // Look for patterns like "'column_name' column"
    const nameMatch = issue.match(/['"`]([^'"`]+)['"`]\s+column/i);
    if (nameMatch) {
        return nameMatch[1];
    }

    return undefined;
}

/**
 * Normalize single problem from raw format
 */
function normalizeProblem(rawProblem: any, index: number): Problem {
    const baseIssue = rawProblem.Issue || rawProblem.issue || '';
    const problemDetails = rawProblem.Problem_Details || rawProblem.Description || rawProblem.description || rawProblem.problem_details || '';
    const columnName = rawProblem.column_name || rawProblem.Column_Name;

    // Create enhanced issue text that includes column name if available
    // Replace first colon with dash for better readability when column name is present
    let formattedIssue = baseIssue;
    if (columnName && baseIssue.includes(':')) {
        formattedIssue = baseIssue.replace(':', ' -');
    }
    const issue = columnName ? `"${columnName}": ${formattedIssue}` : baseIssue;

    const id = generateProblemId(issue, index);
    const kind = deriveProblemKind(baseIssue, problemDetails);

    // Use the criticality field from the API response if available, otherwise fall back to derived severity
    const severity: Severity = rawProblem.criticality?.toLowerCase() as Severity || deriveSeverity(rawProblem);

    // Clean action plan by stripping emojis
    const actionPlan = rawProblem.Action_Plan?.map((step: string) => cleanActionPlanText(step)) ||
        rawProblem.action_plan?.map((step: string) => cleanActionPlanText(step));

    // Normalize technical analysis while keeping nested structure
    let technicalAnalysis;
    const ta = rawProblem.Technical_Analysis || rawProblem.technical_analysis;
    if (ta) {
        technicalAnalysis = {
            missingCount: ta.Missing_Count || ta.missing_count,
            missingPercentage: ta.Missing_Percentage ? parsePercent(ta.Missing_Percentage) :
                (ta.missing_percentage ? parsePercent(ta.missing_percentage) : undefined),
            dataType: ta.Data_Type || ta.data_type,
            sampleValidValues: ta.Sample_Valid_Values || ta.sample_valid_values,
            overallMissingPercentage: ta.Overall_Missing_Percentage ? parsePercent(ta.Overall_Missing_Percentage) :
                (ta.overall_missing_percentage ? parsePercent(ta.overall_missing_percentage) : undefined),
            topMissingColumns: ta.Top_Missing_Columns || ta.top_missing_columns,
            totalAffectedColumns: ta.Total_Affected_Columns || ta.total_affected_columns,
            uniqueValues: ta.Unique_Values || ta.unique_values,
            totalRecords: ta.Total_Records || ta.total_records,
            uniquenessRatio: ta.Uniqueness_Ratio ? parsePercent(ta.Uniqueness_Ratio) :
                (ta.uniqueness_ratio ? parsePercent(ta.uniqueness_ratio) : undefined),
            sampleVariations: ta.Sample_Variations || ta.sample_variations,
            valueDistribution: ta.Value_Distribution || ta.value_distribution,
        };
    }

    return {
        id,
        kind,
        severity,
        issue,
        problemDetails,
        businessImpact: rawProblem.Business_Impact || rawProblem.Impact || rawProblem.business_impact || rawProblem.impact || '',
        technicalAnalysis,
        solutionStrategy: rawProblem.Solution_Strategy || rawProblem.solution_strategy,
        actionPlan,
        referenceValidExamples: rawProblem.Reference_Valid_Examples || rawProblem.reference_valid_examples,
        imputationGuidance: rawProblem.Imputation_Guidance || rawProblem.imputation_guidance,
        category: kind.replace('_', ' '),
        // Additional fields for enhanced UI experience
        affectedRowPercent: technicalAnalysis?.missingPercentage || 0,
        affectedRowCount: technicalAnalysis?.missingCount,
        dimensions: inferDimensions(baseIssue, problemDetails),
        cause: extractCause(problemDetails, ta),
        recommendation: rawProblem.Solution_Strategy || rawProblem.solution_strategy,
        healthScoreDimensionsImpacted: rawProblem.health_score_dimensions_impacted || rawProblem.Health_Score_Dimensions_Impacted || [],
        confidence: inferConfidence(severity),
        tags: generateTags(baseIssue, kind),
        lastAnalyzedAt: new Date().toISOString(),
        location: {
            dataset: 'Current Dataset',
            table: undefined,
            column: columnName || extractColumnFromIssue(baseIssue)
        }
    };
}

/**
 * Generate dimension reason based on score
 */
function generateDimensionReason(dimensionName: string, score: number): string {
    const level = score >= 90 ? 'Excellent' : score >= 80 ? 'Good' : score >= 70 ? 'Fair' : score >= 60 ? 'Poor' : 'Critical';

    const reasons: Record<string, Record<string, string>> = {
        'Accuracy': {
            'Excellent': 'Data values are highly accurate with minimal discrepancies',
            'Good': 'Most data values are accurate with minor issues',
            'Fair': 'Some accuracy issues detected in data values',
            'Poor': 'Multiple accuracy problems affecting data reliability',
            'Critical': 'Significant accuracy issues requiring immediate attention'
        },
        'Validity': {
            'Excellent': 'All data conforms to expected formats and constraints',
            'Good': 'Most data follows proper validation rules',
            'Fair': 'Some validation issues found in data entries',
            'Poor': 'Multiple validation failures detected',
            'Critical': 'Widespread validation issues affecting data integrity'
        },
        'Integrity': {
            'Excellent': 'Data relationships and constraints are well-maintained',
            'Good': 'Strong data integrity with minor inconsistencies',
            'Fair': 'Some integrity issues in data relationships',
            'Poor': 'Multiple integrity violations detected',
            'Critical': 'Serious integrity problems affecting data trustworthiness'
        },
        'Uniqueness': {
            'Excellent': 'Minimal or no duplicate records found',
            'Good': 'Low level of data duplication',
            'Fair': 'Moderate duplication affecting data quality',
            'Poor': 'High levels of duplicate data detected',
            'Critical': 'Extensive duplication compromising data reliability'
        },
        'Consistency': {
            'Excellent': 'Data formats and values are highly consistent',
            'Good': 'Strong consistency with minor variations',
            'Fair': 'Some consistency issues across data fields',
            'Poor': 'Multiple consistency problems detected',
            'Critical': 'Major consistency issues affecting data usability'
        },
        'Completeness': {
            'Excellent': 'Minimal missing data across all fields',
            'Good': 'Low levels of missing data',
            'Fair': 'Moderate amount of missing data present',
            'Poor': 'High levels of missing data detected',
            'Critical': 'Extensive missing data affecting analysis capability'
        }
    };

    return reasons[dimensionName]?.[level] || `${dimensionName} score indicates ${level.toLowerCase()} data quality`;
}

/**
 * Generate dimension evidence based on score
 */
function generateDimensionEvidence(dimensionName: string, score: number): string {
    return `Score: ${score}% - Based on automated data quality assessment algorithms`;
}

/**
 * Parse health score dimensions from raw object
 */
function parseHealthDimensions(healthScoreRaw: Record<string, any>): HealthDimension[] {
    const dimensions: HealthDimension[] = [];

    for (const [key, value] of Object.entries(healthScoreRaw)) {
        if (key === 'Data Quality Assessment (6 Dimensions)' || key === 'data_quality_assessment_6_dimensions' || key === 'overall' || key === 'Dataset Info' || key === 'data_object_description') {
            continue; // Skip overall score entries and metadata
        }

        // Handle nested structure where each dimension has score, reason, evidence
        if (typeof value === 'object' && value !== null && value.score !== undefined) {
            // Check if this is enhanced evidence structure
            let evidenceData: string | EnhancedEvidence = value.evidence || generateDimensionEvidence(key, parsePercent(value.score));

            // If evidence has enhanced structure, preserve it
            if (typeof value.evidence === 'object' && value.evidence !== null &&
                'summary' in value.evidence && 'statistical_details' in value.evidence) {
                evidenceData = value.evidence as EnhancedEvidence;
            }

            dimensions.push({
                key: key.toLowerCase().replace(/\s+/g, '_'),
                label: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
                score: parsePercent(value.score),
                reason: value.reason || generateDimensionReason(key, parsePercent(value.score)),
                evidence: evidenceData
            });
        } else if (typeof value === 'string') {
            // Fallback for old format (string percentages)
            dimensions.push({
                key: key.toLowerCase().replace(/\s+/g, '_'),
                label: key,
                score: parsePercent(value),
            });
        } else if (typeof value === 'number') {
            // Live analysis format (direct numbers) - generate meaningful descriptions
            const reason = generateDimensionReason(key, value);
            const evidence = generateDimensionEvidence(key, value);

            dimensions.push({
                key: key.toLowerCase().replace(/\s+/g, '_'),
                label: key,
                score: parsePercent(value),
                reason: reason,
                evidence: evidence,
            });
        }
    }

    return dimensions;
}

/**
 * Extract dataset info from executive summary
 */
function extractDatasetInfo(executiveSummary: string[]): { rowCount: number; columnCount: number; columnTypes: string } {
    const firstElement = executiveSummary[0];
    const firstLine = typeof firstElement === 'string' ? firstElement : String(firstElement || '');

    const rowMatch = firstLine.match(/(\d{1,3}(?:,\d{3})*)\s+rows?/);
    const colMatch = firstLine.match(/(\d+)\s+columns?/);

    // Extract column types from patterns like "(8 numeric, 19 text, 1 date-related)"
    const columnTypesMatch = firstLine.match(/\(([^)]+)\)/);
    let columnTypes = '';

    if (columnTypesMatch) {
        const typesText = columnTypesMatch[1];
        // Parse individual type counts and format them
        const typeMatches = typesText.match(/(\d+)\s+([^,]+)/g);
        if (typeMatches) {
            const formattedTypes = typeMatches.map(match => {
                const parts = match.trim().split(/\s+/);
                const count = parts[0];
                const type = parts.slice(1).join(' ').replace('-related', '');
                return `${count} ${type}`;
            });
            columnTypes = formattedTypes.join(', ');
        }
    }

    const rowCount = rowMatch ? parseInt(rowMatch[1].replace(/,/g, '')) : 0;
    const columnCount = colMatch ? parseInt(colMatch[1]) : 0;

    return { rowCount, columnCount, columnTypes };
}

/**
 * Normalize prescription priority for sorting
 */
function normalizePrescription(rawPrescription: any, index: number): Prescription {
    return {
        id: `prescription-${index}`,
        priority: rawPrescription.Priority as 'High' | 'Medium' | 'Low',
        focusArea: rawPrescription.Focus_Area,
        summary: rawPrescription.Summary,
        expectedImpact: rawPrescription.Expected_Impact,
    };
}

/**
 * Group prescriptions by priority for UI display
 */
export function groupPrescriptions(prescriptions: Prescription[]): GroupedPrescriptions {
    return {
        high: prescriptions.filter(p => p.priority === 'High'),
        medium: prescriptions.filter(p => p.priority === 'Medium'),
        low: prescriptions.filter(p => p.priority === 'Low'),
    };
}

/**
 * Extract and separate next steps from key findings
 */
function extractNextStepsFromFindings(findings: string[]): { keyFindings: string[]; nextSteps: string[] } {
    const keyFindings: string[] = [];
    const nextSteps: string[] = [];

    findings.forEach(finding => {
        // Check if this finding contains "Next Steps"
        if (finding.includes('Next Steps')) {
            // Extract the next steps content after "Next Steps"
            const nextStepsMatch = finding.match(/Next Steps\s+(.+)/s);
            if (nextStepsMatch) {
                const nextStepsText = nextStepsMatch[1];
                // Split into bullet points by sentence endings, but be more careful
                const steps = nextStepsText
                    .split(/\.\s+(?=[A-Z]|Next|Finally|First|Second|Third)/) // Split on periods followed by capital letters or specific transition words
                    .map(step => step.trim())
                    .filter(step => step.length > 10) // Filter out very short fragments
                    .filter(step => !step.match(/^[-\s]*$/)) // Filter out lines that are just dashes or spaces
                    .map(step => {
                        // Clean up any trailing dashes or extra punctuation
                        step = step.replace(/[-\s]*$/, '');
                        // Ensure proper ending punctuation
                        return step.endsWith('.') ? step : step + '.';
                    })
                    .filter(step => step.length > 2); // Final filter for meaningful content

                nextSteps.push(...steps);
            }
        } else {
            keyFindings.push(finding);
        }
    });

    return { keyFindings, nextSteps };
}

/**
 * Main normalization function: raw payload -> domain model
 */
export function normalizeReport(raw: RawReportPayload, dataObjectName?: string): Report {
    const dataObjectDescription = raw['Data Object Description'];
    const executiveSummary = raw['Executive Summary'] || [];
    const healthScoreRaw = raw['Health Score'] || {};
    const problemsRaw = raw['Detailed Problems'] || [];
    const prescriptionsRaw = raw['Detailed Prescription'] || [];
    const reasoningResult = raw.reasoning_result;

    // Extract executive summary object for dynamic rendering
    let executiveSummaryObject: { [key: string]: string } | undefined;

    // Check if we already have the executive_summary object from raw API response
    if (raw.executive_summary && typeof raw.executive_summary === 'object' && !Array.isArray(raw.executive_summary)) {
        // Raw API format: executive_summary is an object
        executiveSummaryObject = raw.executive_summary as { [key: string]: string };
    } else if (executiveSummary.length > 0) {
        if (typeof executiveSummary[0] === 'object' && executiveSummary[0] !== null) {
            // Historical format: array with object
            executiveSummaryObject = executiveSummary[0] as { [key: string]: string };
        } else if (typeof executiveSummary[0] === 'string') {
            // Live analysis format: array of strings, parse them into object
            executiveSummaryObject = {};

            executiveSummary.forEach((item: string) => {
                // Parse strings like "Dataset Overview: This dataset supports..."
                const colonIndex = item.indexOf(':');
                if (colonIndex > 0) {
                    const key = item.substring(0, colonIndex).trim();
                    const value = item.substring(colonIndex + 1).trim();
                    executiveSummaryObject![key] = value;
                }
            });
        }
    }

    // Extract dataset info - prioritize Data Object Description if available
    let rowCount = 0;
    let columnCount = 0;
    let description = '';
    let columnTypes = '';
    let totalDiagnosisProblems = 0;

    // Check for Data Object Description in multiple possible locations
    const dataObjDesc = dataObjectDescription || healthScoreRaw.data_object_description;

    if (dataObjDesc && typeof dataObjDesc === 'object' &&
        ((dataObjDesc as any).total_rows !== undefined ||
            (dataObjDesc as any).total_columns !== undefined ||
            (dataObjDesc as any).description)) {
        rowCount = (dataObjDesc as any).total_rows || 0;
        columnCount = (dataObjDesc as any).total_columns || 0;
        description = (dataObjDesc as any).description || '';
        columnTypes = (dataObjDesc as any).column_types || '';
        totalDiagnosisProblems = (dataObjDesc as any).total_diagnosis_problems || problemsRaw.length || 0;
    } else {
        // Fallback to extracting from executive summary - but only if it looks like parseable data
        try {
            const firstElement = executiveSummary[0];
            const hasParseableContent = firstElement &&
                typeof firstElement === 'string' &&
                (firstElement.includes('rows') || firstElement.includes('columns'));

            if (hasParseableContent) {
                const extracted = extractDatasetInfo(executiveSummary);
                rowCount = extracted.rowCount;
                columnCount = extracted.columnCount;
                columnTypes = extracted.columnTypes;

            } else {

                // Use safe defaults
                rowCount = 0;
                columnCount = 0;
                columnTypes = '';
                totalDiagnosisProblems = 0;
            }
        } catch (error) {
            console.error('reportTransform - error extracting dataset info:', error);
            // Use safe defaults
            rowCount = 0;
            columnCount = 0;
            columnTypes = '';
            totalDiagnosisProblems = 0;
        }
    }

    // Parse health score - handle both live analysis and historical formats
    let overallScore = 0;

    // Check for live analysis format with data_quality_assessment_6_dimensions
    if (healthScoreRaw.data_quality_assessment_6_dimensions) {
        const scoreValue = healthScoreRaw.data_quality_assessment_6_dimensions;
        // Handle both string/number and object formats
        const actualValue = (typeof scoreValue === 'object' && scoreValue && 'score' in scoreValue)
            ? (scoreValue as any).score
            : scoreValue;
        overallScore = parsePercent(actualValue);
    } else {
        // Historical format
        const overallScoreValue = healthScoreRaw['Data Quality Assessment (6 Dimensions)'];
        // Handle both string/number and object formats
        const actualValue = (typeof overallScoreValue === 'object' && overallScoreValue && 'score' in overallScoreValue)
            ? (overallScoreValue as any).score
            : overallScoreValue;
        overallScore = parsePercent(actualValue);
    }

    const dimensions = parseHealthDimensions(healthScoreRaw);

    // Normalize problems - show all problems from JSON without filtering
    let problems = problemsRaw.map((problem, index) => normalizeProblem(problem, index));

    // Fallback: populate problems from reasoning_result if Detailed Problems are absent
    if (problems.length === 0 && reasoningResult?.problematic_columns?.length) {
        const reasoningDerived: Problem[] = [];
        reasoningResult.problematic_columns.forEach((col, colIdx) => {
            col.issues.forEach((issue, issueIdx) => {
                // Map severity to existing Severity type
                const sevUpper = (issue.severity || '').toUpperCase();
                const severity: Severity =
                    sevUpper === 'CRITICAL' ? 'critical' :
                        sevUpper === 'HIGH' ? 'high' :
                            sevUpper === 'MEDIUM' ? 'medium' :
                                sevUpper === 'LOW' ? 'low' : 'medium';

                // Infer kind from issue_type text heuristically
                const issueType = (issue.issue_type || '').toLowerCase();
                let kind: ProblemKind = 'pattern_change';
                if (issueType.includes('missing') || issueType.includes('null')) kind = 'column_completeness';
                else if (issueType.includes('standard') || issueType.includes('format')) kind = 'standardization';

                const confidenceValue = (issue.confidence || '').toLowerCase();
                const confidence: Problem['confidence'] =
                    confidenceValue === 'low' || confidenceValue === 'medium' || confidenceValue === 'high'
                        ? confidenceValue as Problem['confidence']
                        : undefined;

                reasoningDerived.push({
                    id: `reasoning-${colIdx}-${issueIdx}`,
                    kind,
                    severity,
                    issue: issue.brief_description || issue.reason || issue.description || 'Issue',
                    problemDetails: issue.description || issue.brief_description || '',
                    businessImpact: issue.business_impact || '',
                    technicalAnalysis: undefined,
                    solutionStrategy: issue.remediation?.solution_strategy || '',
                    actionPlan: issue.remediation?.action_plan || [],
                    affectedColumns: [col.column_name],
                    category: issueType || 'diagnostic',
                    affectedRowPercent: undefined,
                    affectedRowCount: undefined,
                    dimensions: undefined,
                    cause: issue.root_cause_analysis || '',
                    recommendation: issue.remediation?.solution_strategy || '',
                    healthScoreDimensionsImpacted: issue.health_score_dimensions_impacted || issue.remediation?.health_score_dimensions_impacted || [],
                    confidence,
                    tags: undefined,
                    lastAnalyzedAt: new Date().toISOString(),
                    location: { column: col.column_name, dataset: dataObjectDescription?.dataset_name || dataObjectName }
                });
            });
        });
        problems = reasoningDerived;
    }

    // Normalize prescriptions
    const prescriptions = prescriptionsRaw.map((prescription, index) => normalizePrescription(prescription, index));

    // Use data_object_name from API response directly
    let datasetName = (raw as any).data_object_name || dataObjectName || 'Dataset Analysis';

    const finalReport = {
        id: 'report-1', // TODO: Make dynamic when backend provides
        generatedAt: new Date().toISOString(),
        dataset: {
            id: 'dataset-1', // TODO: Make dynamic
            name: datasetName, // Use data_object_name from API
            rowCount,
            columnCount,
            description,
            columnTypes,
            totalDiagnosisProblems,
        },
        healthScore: {
            overall: overallScore,
            dimensions,
        },
        summary: {
            headline: executiveSummary[0] || 'Data quality assessment completed',
            ...extractNextStepsFromFindings(executiveSummary.slice(1)),
        },
        problems,
        prescriptions,
        dataObjectDescription: dataObjectDescription && typeof dataObjectDescription === 'object' &&
            ((dataObjectDescription as any).total_rows !== undefined ||
                (dataObjectDescription as any).total_columns !== undefined ||
                (dataObjectDescription as any).description) ?
            {
                ...(dataObjectDescription as any),
                total_rows: (dataObjectDescription as any).total_rows || 0,
                total_columns: (dataObjectDescription as any).total_columns || 0,
                description: (dataObjectDescription as any).description || '',
                column_types: (dataObjectDescription as any).column_types || '',
                table_type: (dataObjectDescription as any).table_type || '',
                total_diagnosis_problems: (dataObjectDescription as any).total_diagnosis_problems || 0,
                dataset_name: (dataObjectDescription as any).dataset_name || ''
            } :
            {
                dataset_name: dataObjectName || 'Data Analysis',
                description: description || 'No description available',
                total_rows: rowCount,
                total_columns: columnCount,
                column_types: columnTypes,
                table_type: '',
                total_diagnosis_problems: totalDiagnosisProblems
            },
        executiveSummary: executiveSummaryObject,
        reasoningResult: reasoningResult,
        dq_framework_results: raw.dq_framework_results, // Preserve DQ framework results for UserPrescribedRules component
    };



    return finalReport;
}